import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	List,
	ListItem,
	ListItemText,
	Stack,
	Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PAYPAL_CSV_COLUMNS } from "@/configConstants";
import HockeyPlannerRoot from "../../components/HockeyPlannerRoot";
import { createFrontendTrpcClient } from "../../utils";
import { useAuthStatus } from "../eventslist/hooks";
import PaymentDataGrid from "./PaymentDataGrid";
import {
	applyPaymentMatchingToRows,
	convertPayPalDatetimeToISO,
	createInitialPaymentRows,
	defaultStats,
	type FilterStats,
	filterRows,
	getValue,
	type ParsedRow,
	type PaymentRow,
	type PayPalPayment,
	validatePaymentRows,
} from "./payment-import-logic";
import UploadCard from "./UploadCard";

const trpcClient = createFrontendTrpcClient();

export default function PaymentsImportView() {
	const {
		data: meData,
		error: meError,
		isLoading: meLoading,
	} = useAuthStatus(() => trpcClient.user.getOwn.query());

	const {
		data: importData,
		isLoading: importLoading,
		error: importError,
	} = useQuery({
		queryKey: ["payment-import-data"],
		queryFn: () => trpcClient.payment.getPaymentImportData.query(),
		enabled: !!meData && !meError && (meData?.user?.is_manager ?? false),
	});

	const isManager = meData?.user?.is_manager ?? false;
	const loggedIn = !!meData && !meError;
	const missingManager = useMemo(
		() => !meLoading && (!loggedIn || !isManager),
		[isManager, loggedIn, meLoading],
	);

	const [rawRows, setRawRows] = useState<ParsedRow[] | null>(null);
	const [paypalPayments, setPaypalPayments] = useState<PayPalPayment[] | null>(
		null,
	);
	const [paymentRows, setPaymentRows] = useState<PaymentRow[] | null>(null);
	const [filterStats, setFilterStats] = useState<FilterStats>(defaultStats);
	const [validationDialogOpen, setValidationDialogOpen] = useState(false);
	const [validationErrors, setValidationErrors] = useState<string[]>([]);
	const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

	const importedCodes = useMemo(
		() => new Set(importData?.importedTransactionCodes ?? []) as Set<string>,
		[importData?.importedTransactionCodes],
	);

	const handleParsed = useCallback((rows: ParsedRow[]) => {
		setRawRows(rows);
	}, []);

	useEffect(() => {
		if (!rawRows || !importData) return;
		const { filtered, stats } = filterRows(rawRows, importedCodes);

		// Create initial payment rows (one per PayPal payment)
		const initialPaymentRows = createInitialPaymentRows(filtered);

		// Create map for quick lookup
		const paypalMap = new Map(filtered.map((pp) => [pp.transactionCode, pp]));

		// Apply auto-matching to populate user and event fields
		const matchedRows = applyPaymentMatchingToRows(
			initialPaymentRows,
			paypalMap,
			importData,
		);

		setPaypalPayments(filtered);
		setPaymentRows(matchedRows);
		setFilterStats(stats);
	}, [rawRows, importedCodes, importData]);

	const handleRemoveRow = useCallback((paymentId: string) => {
		setPaymentRows((prev) =>
			prev ? prev.filter((r) => r.id !== paymentId) : prev,
		);
	}, []);

	const handleSplitPayment = useCallback(
		(paymentId: string, amounts: number[]) => {
			setPaymentRows((prev) => {
				if (!prev) return prev;

				const paymentToSplit = prev.find((p) => p.id === paymentId);
				if (!paymentToSplit) return prev;

				// Remove the original payment
				const withoutOriginal = prev.filter((p) => p.id !== paymentId);

				// Create new split payments, preserving checked state
				const splitPayments: PaymentRow[] = amounts.map(
					(amountCents, index) => ({
						id: `${paymentToSplit.paypalTransactionCode}-${Date.now()}-${index}`,
						paypalTransactionCode: paymentToSplit.paypalTransactionCode,
						amountCents,
						userId: null,
						eventId: "",
						costPerParticipant: paymentToSplit.costPerParticipant,
						amountIndicator: null, // Will be recalculated when user/event assigned
						checked: paymentToSplit.checked ?? true, // Preserve checked state
					}),
				);

				return [...withoutOriginal, ...splitPayments];
			});
		},
		[],
	);

	const handleUpdatePaymentRow = useCallback(
		(paymentId: string, updates: Partial<PaymentRow>) => {
			setPaymentRows((prev) => {
				if (!prev) return prev;
				return prev.map((row) =>
					row.id === paymentId ? { ...row, ...updates } : row,
				);
			});
		},
		[],
	);

	const handleToggleCheck = useCallback(
		(paymentId: string, checked: boolean) => {
			setPaymentRows((prev) => {
				if (!prev) return prev;
				return prev.map((row) =>
					row.id === paymentId ? { ...row, checked } : row,
				);
			});
		},
		[],
	);

	const handleToggleAllChecked = useCallback((checked: boolean) => {
		setPaymentRows((prev) => {
			if (!prev) return prev;
			return prev.map((row) => ({ ...row, checked }));
		});
	}, []);

	const resetUpload = useCallback(() => {
		setRawRows(null);
		setPaypalPayments(null);
		setPaymentRows(null);
		setFilterStats(defaultStats);
	}, []);

	const importMutation = useMutation({
		mutationFn: async () => {
			if (!paymentRows || !paypalPayments || !importData) {
				throw new Error("Missing data for import");
			}

			// Convert paypalPayments array to Map for easier lookup
			const paypalPaymentsMap = new Map(
				paypalPayments.map((pp) => [pp.transactionCode, pp]),
			);

			// Build payments list first to know which PayPal payments are referenced
			const paymentsList = paymentRows
				.filter(
					(row) =>
						(row.checked === true || row.checked === undefined) && row.userId,
				) // Only include checked rows with assigned users
				.map((row) => {
					const pp = paypalPaymentsMap.get(row.paypalTransactionCode);
					if (!pp)
						throw new Error(
							`PayPal payment not found: ${row.paypalTransactionCode}`,
						);

					const iso = convertPayPalDatetimeToISO(
						String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.DATE) ?? ""),
						String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.TIME) ?? "00:00:00"),
					);

					return {
						paypal_transaction_code: row.paypalTransactionCode,
						user_id: row.userId ?? "",
						event_id:
							row.eventId && row.eventId !== ""
								? typeof row.eventId === "number"
									? row.eventId
									: Number.parseInt(String(row.eventId), 10)
								: undefined,
						amount_cents: row.amountCents,
						type: "paypal" as const,
						datetime: iso,
					};
				});

			// Extract unique transaction codes from selected payments
			const referencedTransactionCodes = new Set(
				paymentsList.map((p) => p.paypal_transaction_code),
			);

			// Only include PayPal payments that are actually referenced by selected payment rows
			const paypalPaymentsList = Array.from(referencedTransactionCodes).map(
				(transactionCode) => {
					const pp = paypalPaymentsMap.get(transactionCode);
					if (!pp)
						throw new Error(`PayPal payment not found: ${transactionCode}`);

					const iso = convertPayPalDatetimeToISO(
						String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.DATE) ?? ""),
						String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.TIME) ?? "00:00:00"),
						String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.TIMEZONE) ?? ""),
					);

					return {
						transaction_code: pp.transactionCode,
						datetime: iso,
						payer_email: String(
							getValue(pp.raw, PAYPAL_CSV_COLUMNS.PAYER_EMAIL) ?? "",
						),
						payer_name: String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.NAME) ?? ""),
						payer_phone: getValue(pp.raw, PAYPAL_CSV_COLUMNS.PHONE)
							? String(getValue(pp.raw, PAYPAL_CSV_COLUMNS.PHONE))
							: undefined,
						amount_cents: pp.amountCents,
						description: String(
							getValue(pp.raw, PAYPAL_CSV_COLUMNS.NOTE) ?? "",
						),
					};
				},
			);

			return trpcClient.payment.importPayments.mutate({
				paypalPayments: paypalPaymentsList,
				payments: paymentsList,
			});
		},
		onSuccess: () => {
			// Reset the form on successful import
			resetUpload();
		},
	});

	const handleSave = useCallback(() => {
		if (!paymentRows || !importData) return;

		// Run validation
		const validation = validatePaymentRows(paymentRows, importData);

		if (!validation.valid || validation.warnings.length > 0) {
			setValidationErrors(validation.errors);
			setValidationWarnings(validation.warnings);
			setValidationDialogOpen(true);
		} else {
			// All good, proceed with import
			importMutation.mutate();
		}
	}, [paymentRows, importData, importMutation]);

	const handleConfirmSave = useCallback(() => {
		setValidationDialogOpen(false);
		if (validationErrors.length === 0) {
			// Only warnings, user confirmed
			importMutation.mutate();
		}
	}, [validationErrors, importMutation]);

	const handleCancelSave = useCallback(() => {
		setValidationDialogOpen(false);
		setValidationErrors([]);
		setValidationWarnings([]);
	}, []);

	return (
		<HockeyPlannerRoot title="Zahlungen importieren" showBackButton={true}>
			<Box
				sx={{
					px: { xs: 2, md: 4 },
					py: { xs: 3, md: 5 },
					width: "100%",
					maxWidth: 1500,
					mx: "auto",
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					flex: 1,
				}}
			>
				{meLoading ? (
					<Stack alignItems="center" spacing={2}>
						<CircularProgress />
						<Typography variant="body1">Lade Benutzerstatus...</Typography>
					</Stack>
				) : missingManager ? (
					<Alert severity="warning">
						Nur Manager koennen Zahlungen importieren. Bitte melde dich mit
						einem berechtigten Konto an.
					</Alert>
				) : importLoading ? (
					<Stack alignItems="center" spacing={2}>
						<CircularProgress />
						<Typography variant="body1">Lade Import-Daten...</Typography>
					</Stack>
				) : importError ? (
					<Alert severity="error">
						Import-Daten konnten nicht geladen werden.
					</Alert>
				) : rawRows ? (
					<Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
					{!paymentRows && (
						<Card>
							<CardContent>
								<Stack spacing={2}>
									<Typography variant="h6">CSV verarbeitet</Typography>
									<Typography variant="body2">
										Zeilen gesamt: {rawRows.length}
									</Typography>
									<Typography variant="body2">
										Nach Filtern: {paymentRows?.length ?? 0}
									</Typography>
									{paymentRows && (
										<Typography variant="body2" color="text.secondary">
											Gefiltert: importiert {filterStats.imported}, doppelt{" "}
											{filterStats.duplicate}, fehlender Code{" "}
											{filterStats.missingCode}, ungueltiger Betrag{" "}
											{filterStats.invalidAmount}, nicht eingehend{" "}
											{filterStats.nonIncoming}, Typ ausgeschlossen{" "}
											{filterStats.typeFiltered}
										</Typography>
									)}
									<Button variant="outlined" onClick={resetUpload}>
										Neue Datei laden
									</Button>
								</Stack>
							</CardContent>
						</Card>
						)}
						{paymentRows && paypalPayments && importData && (
							<>
								<PaymentDataGrid
									paymentRows={paymentRows}
									paypalPayments={
										new Map(
											paypalPayments.map((pp) => [pp.transactionCode, pp]),
										)
									}
									importData={importData}
									onRemoveRow={handleRemoveRow}
									onSplitPayment={handleSplitPayment}
									onUpdateRow={handleUpdatePaymentRow}
									onToggleCheck={handleToggleCheck}
									onToggleAllChecked={handleToggleAllChecked}
								/>
								<Box
									sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}
								>
									<Button
										variant="contained"
										color="primary"
										size="large"
										onClick={handleSave}
										disabled={importMutation.isPending}
									>
										{importMutation.isPending ? "Speichern..." : "Speichern"}
									</Button>
								</Box>
								{importMutation.isError && (
									<Alert severity="error" sx={{ mt: 2 }}>
										Fehler beim Speichern der Zahlungen:{" "}
										{importMutation.error instanceof Error
											? importMutation.error.message
											: "Unbekannter Fehler"}
									</Alert>
								)}
								{importMutation.isSuccess && (
									<Alert severity="success" sx={{ mt: 2 }}>
										Zahlungen erfolgreich importiert!
									</Alert>
								)}
							</>
						)}
						<Dialog
							open={validationDialogOpen}
							onClose={handleCancelSave}
							maxWidth="md"
							fullWidth
						>
							<DialogTitle>
								{validationErrors.length > 0
									? "Validierungsfehler"
									: "Validierungswarnungen"}
							</DialogTitle>
							<DialogContent>
								{validationErrors.length > 0 && (
									<>
										<DialogContentText color="error" sx={{ mb: 2 }}>
											Behebe die folgenden Fehler vor dem Speichern:
										</DialogContentText>
										<List dense>
											{validationErrors.map((error) => (
												<ListItem key={error}>
													<ListItemText primary={error} />
												</ListItem>
											))}
										</List>
									</>
								)}
								{validationWarnings.length > 0 && (
									<>
										<DialogContentText
											color="warning.main"
											sx={{ mb: 2, mt: validationErrors.length > 0 ? 2 : 0 }}
										>
											{validationErrors.length > 0
												? "Zusätzlich wurden die folgenden Warnungen gefunden:"
												: "Die folgenden Warnungen wurden gefunden. Trotzdem fortfahren?"}
										</DialogContentText>
										<List dense>
											{validationWarnings.map((warning) => (
												<ListItem key={warning}>
													<ListItemText primary={warning} />
												</ListItem>
											))}
										</List>
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button onClick={handleCancelSave}>Abbrechen</Button>
								{validationErrors.length === 0 && (
									<Button
										onClick={handleConfirmSave}
										color="primary"
										variant="contained"
									>
										Trotzdem fortfahren
									</Button>
								)}
							</DialogActions>
						</Dialog>
					</Stack>
				) : (
					<UploadCard onParsed={handleParsed} />
				)}
			</Box>
		</HockeyPlannerRoot>
	);
}
